import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Loader2, ChevronDown, ChevronRight, CheckCircle2,
  Truck, Sparkles, Check, Search, Layers, Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';
import { ExcelPickSlipUpload } from './ExcelPickSlipUpload';

const SeasonalCreatePickSlip = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState('');
  const [selPlace, setSelPlace] = useState('');
  const [custOpen, setCustOpen] = useState(false);
  const [slugTree, setSlugTree] = useState([]); // [{slug, available_boxes, boxes:[...]}]
  const [treeLoading, setTreeLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [expandedSlugs, setExpandedSlugs] = useState(() => new Set());
  const [expandedBoxes, setExpandedBoxes] = useState(() => new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [halnode, setHalnode] = useState('');
  const [dcNo, setDcNo] = useState('');
  const [transporters, setTransporters] = useState([]);
  const [transportId, setTransportId] = useState('');
  const [recommendation, setRecommendation] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  const place = selPlace;

  const loadCustomers = useCallback(async () => {
    try { const r = await api.getPickSlipCustomers(); setCustomers(r.customers || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load customers'); }
  }, []);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const loadTransports = useCallback(async () => {
    try { const r = await api.getActiveTransports(); setTransporters(r.transports || []); }
    catch (e) { /* dropdown just stays empty */ }
  }, []);
  useEffect(() => { loadTransports(); }, [loadTransports]);

  const loadTree = useCallback(async (c, p) => {
    setTreeLoading(true); setSlugTree([]);
    try {
      const r = await api.getPickSlipSlugTree(c, p);
      const tree = r.slugs || [];
      setSlugTree(tree);
      // Expand the first slug branch by default for quick access.
      setExpandedSlugs(new Set(tree.length ? [tree[0].slug] : []));
    } catch (e) {
      toast.error('Failed to load slugs & boxes');
    } finally { setTreeLoading(false); }
  }, []);

  const pickCustomer = (c) => {
    setCustomer(c.customer); setSelPlace(c.place || '');
    setSelected(new Set()); setExpandedBoxes(new Set());
    setCustOpen(false);
    loadTree(c.customer, c.place || '');
  };

  const toggleBox = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSlugExpand = (s) => setExpandedSlugs((p) => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const toggleBoxExpand = (id) => setExpandedBoxes((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const slugBoxIds = (node) => node.boxes.map((b) => b.box_id);
  const slugSelectedCount = (node) => node.boxes.reduce((a, b) => a + (selected.has(b.box_id) ? 1 : 0), 0);
  const toggleSlugAll = (node) => setSelected((p) => {
    const n = new Set(p);
    const ids = slugBoxIds(node);
    const allOn = ids.every((id) => n.has(id));
    ids.forEach((id) => (allOn ? n.delete(id) : n.add(id)));
    return n;
  });

  const allBoxIds = useMemo(() => slugTree.flatMap((s) => s.boxes.map((b) => b.box_id)), [slugTree]);
  const toggleSelectAll = () => setSelected((p) => (p.size >= allBoxIds.length && allBoxIds.length > 0 ? new Set() : new Set(allBoxIds)));

  const selectedSlugCount = useMemo(
    () => slugTree.filter((s) => s.boxes.some((b) => selected.has(b.box_id))).length,
    [slugTree, selected],
  );

  // When the DC-tag dialog opens, auto-suggest a transporter for the selected boxes.
  useEffect(() => {
    if (!dialogOpen || selected.size === 0) return;
    let cancelled = false;
    (async () => {
      setRecoLoading(true); setRecommendation(null);
      try {
        const res = await api.recommendTransport({ customer, box_ids: Array.from(selected) });
        if (cancelled) return;
        setRecommendation(res);
        if (res.recommended_transport_id) setTransportId(res.recommended_transport_id);
      } catch (e) {
        if (!cancelled) setRecommendation({ error: e.response?.data?.detail || 'Could not compute a recommendation' });
      } finally { if (!cancelled) setRecoLoading(false); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const closeDialog = () => { setDialogOpen(false); setRecommendation(null); };

  const create = async () => {
    if (!halnode.trim()) { toast.error('Halnode Pick Slip No is required'); return; }
    if (!dcNo.trim()) { toast.error('DC Number is required'); return; }
    if (!transportId) { toast.error('Select a Transport'); return; }
    const transport = transporters.find((t) => t.transport_id === transportId);
    setCreating(true);
    try {
      const res = await api.createPickSlip({
        customer, place, box_ids: Array.from(selected),
        halnode_pick_slip_no: halnode.trim(), dc_number: dcNo.trim(),
        transport_id: transportId, transport_name: transport?.name || '',
      });
      toast.success(res.message);
      setCreated(res.pick_slip);
      setDialogOpen(false); setHalnode(''); setDcNo(''); setTransportId(''); setRecommendation(null);
      setSelected(new Set());
      await loadTree(customer, place); // reserved boxes drop out of the tree
      await loadCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create pick slip');
    } finally { setCreating(false); }
  };

  const customerLabel = customer
    ? `${customer}${place ? ` — ${place}` : ' — (no place)'}`
    : 'Select customer…';

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-create-pickslip-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="scp-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ClipboardList className="w-6 h-6 mr-3 text-indigo-400" />
          <div>
            <h1 className="text-xl font-bold font-display">Create Pick Slip</h1>
            <p className="text-sm text-[#E8DCC8]">Customer → slugs (with packed bundles) → select across slugs → pick slip</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <Tabs defaultValue="manual" className="space-y-5">
          <TabsList data-testid="cps-tabs">
            <TabsTrigger value="manual" data-testid="cps-tab-manual">Manual select</TabsTrigger>
            <TabsTrigger value="excel" data-testid="cps-tab-excel">Upload Excel</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-5">
        {created && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="scp-created-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-800">Pick slip created — {created.box_count} box(es)</p>
              <p className="text-emerald-700">Halnode {created.halnode_pick_slip_no} · DC {created.dc_number}
                {created.assigned_picker_name ? ` · assigned to ${created.assigned_picker_name}` : ' · not assigned (set a Seasonal picker)'}</p>
            </div>
          </div>
        )}

        {/* Searchable customer selector */}
        <Card>
          <CardContent className="pt-5">
            <label className="text-xs text-[#5A5F6E]">Customer</label>
            <Popover open={custOpen} onOpenChange={setCustOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={custOpen}
                  className="w-full justify-between font-normal"
                  data-testid="scp-customer-combobox"
                >
                  <span className={customer ? 'text-[#1A1F2E]' : 'text-[#8A8F9E]'}>{customerLabel}</span>
                  <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
                <Command>
                  <div className="flex items-center border-b px-3">
                    <Search className="w-4 h-4 mr-2 shrink-0 opacity-50" />
                    <CommandInput placeholder="Search customer or place…" className="h-10 border-0 focus:ring-0" data-testid="scp-customer-search" />
                  </div>
                  <CommandList>
                    <CommandEmpty>No customer found.</CommandEmpty>
                    <CommandGroup>
                      {customers.map((c) => {
                        const key = `${c.customer}|||${c.place}`;
                        const isSel = c.customer === customer && (c.place || '') === (place || '');
                        return (
                          <CommandItem
                            key={key}
                            value={`${c.customer} ${c.place || ''}`}
                            onSelect={() => pickCustomer(c)}
                            data-testid={`scp-customer-opt-${c.customer}-${c.place || 'noplace'}`}
                          >
                            <Check className={`w-4 h-4 mr-2 ${isSel ? 'opacity-100 text-indigo-600' : 'opacity-0'}`} />
                            <span className="flex-1">{c.customer}{c.place ? ` — ${c.place}` : ' — (no place)'}</span>
                            <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px] ml-2">{c.available_boxes} boxes</Badge>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {customer && (
              <p className="text-xs text-[#8A8F9E] mt-1">
                {place ? `Place: ${place}` : 'No place'} · pick boxes from any slug below
              </p>
            )}
          </CardContent>
        </Card>

        {/* Slug → boxes tree (select across slugs) */}
        {customer && (
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b flex items-center gap-3 bg-gray-50 flex-wrap">
                <Checkbox
                  checked={allBoxIds.length > 0 && selected.size >= allBoxIds.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="scp-select-all"
                />
                <span className="text-sm font-medium">Select all</span>
                <span className="text-xs text-[#8A8F9E]" data-testid="scp-selected-counter">
                  {selected.size} box(es) selected across {selectedSlugCount} slug(s)
                </span>
                <div className="flex-1" />
                <Button
                  disabled={selected.size === 0}
                  onClick={() => setDialogOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="scp-create-btn"
                >
                  Create Pick Slip ({selected.size})
                </Button>
              </div>

              {treeLoading ? (
                <p className="text-center text-[#5A5F6E] py-10"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading slugs & boxes…</p>
              ) : slugTree.length === 0 ? (
                <p className="text-center text-[#5A5F6E] py-10" data-testid="scp-no-slugs">No packed bundles available for this customer.</p>
              ) : (
                <div className="divide-y" data-testid="scp-slug-tree">
                  {slugTree.map((node) => {
                    const open = expandedSlugs.has(node.slug);
                    const selCount = slugSelectedCount(node);
                    const allOn = node.boxes.length > 0 && selCount === node.boxes.length;
                    return (
                      <div key={node.slug || '__noslug__'} data-testid={`scp-slug-branch-${node.slug || 'noslug'}`}>
                        {/* Slug branch header */}
                        <div className="px-4 py-2.5 flex items-center gap-3 bg-indigo-50/60">
                          <Checkbox
                            checked={allOn}
                            onCheckedChange={() => toggleSlugAll(node)}
                            data-testid={`scp-slug-check-${node.slug || 'noslug'}`}
                          />
                          <button onClick={() => toggleSlugExpand(node.slug)} className="flex items-center gap-2 flex-1 text-left" data-testid={`scp-slug-expand-${node.slug || 'noslug'}`}>
                            {open ? <ChevronDown className="w-4 h-4 text-indigo-500" /> : <ChevronRight className="w-4 h-4 text-indigo-500" />}
                            <Layers className="w-4 h-4 text-indigo-500" />
                            <span className="font-semibold text-sm text-[#1A1F2E]">{node.slug || '(no slug)'}</span>
                            <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[11px]">{node.available_boxes} boxes</Badge>
                            {selCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">{selCount} selected</Badge>}
                          </button>
                        </div>

                        {/* Boxes under this slug */}
                        {open && (
                          <div className="divide-y bg-white">
                            {node.boxes.map((b) => {
                              const bopen = expandedBoxes.has(b.box_id);
                              return (
                                <div key={b.box_id} className="pl-8" data-testid={`scp-box-${b.box_id}`}>
                                  <div className="px-4 py-2 flex items-center gap-3">
                                    <Checkbox checked={selected.has(b.box_id)} onCheckedChange={() => toggleBox(b.box_id)} data-testid={`scp-box-check-${b.box_id}`} />
                                    <button onClick={() => toggleBoxExpand(b.box_id)} className="flex items-center gap-2 flex-1 text-left" data-testid={`scp-box-expand-${b.box_id}`}>
                                      {bopen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                      <Package className="w-4 h-4 text-sky-600" />
                                      <span className="font-mono text-sm font-medium">{b.box_number}</span>
                                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[11px]">{b.footprint || 'no bin'}</Badge>
                                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">{b.total_qty} pcs · {b.items.length} items</Badge>
                                    </button>
                                  </div>
                                  {bopen && (
                                    <div className="px-10 pb-3">
                                      <table className="w-full text-xs">
                                        <thead><tr className="text-left text-[#8A8F9E]">
                                          <th className="py-1">Barcode</th><th>Design</th><th>Shade</th><th>Size</th><th>Description</th><th className="text-right">Qty</th>
                                        </tr></thead>
                                        <tbody>
                                          {(b.items || []).map((it, i) => (
                                            <tr key={i} className="border-t">
                                              <td className="py-1 font-mono">{it.barcode}</td><td>{it.design_name}</td>
                                              <td>{it.shade}</td><td>{it.size}</td><td>{it.description}</td>
                                              <td className="text-right font-semibold">{it.qty}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </TabsContent>

          <TabsContent value="excel">
            <ExcelPickSlipUpload transporters={transporters} onCreated={loadCustomers} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); else setDialogOpen(true); }}>
        <DialogContent data-testid="scp-dialog">
          <DialogHeader>
            <DialogTitle>Create Pick Slip — {selected.size} box(es)</DialogTitle>
            <DialogDescription>Tag the DC details and transport for the selected boxes, then create the outward pick slip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5A5F6E]">{customer}{place ? ` — ${place}` : ''} · {selectedSlugCount} slug(s)</p>
            <div>
              <label className="text-xs text-[#5A5F6E]">Halnode Pick Slip No *</label>
              <Input value={halnode} onChange={(e) => setHalnode(e.target.value)} placeholder="Enter Halnode pick slip number" data-testid="scp-halnode-input" />
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">DC Number *</label>
              <Input value={dcNo} onChange={(e) => setDcNo(e.target.value)} placeholder="Enter DC number" data-testid="scp-dc-input" />
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Transport *</label>
              <div className="mb-2 rounded-md border border-[#E5DFD0] bg-[#F7F4EC] px-3 py-2" data-testid="scp-reco-panel">
                {recoLoading ? (
                  <p className="text-xs text-[#5A5F6E] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Computing best transporter…</p>
                ) : recommendation?.error ? (
                  <p className="text-xs text-amber-700" data-testid="scp-reco-error">{recommendation.error}</p>
                ) : recommendation ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs text-[#5A5F6E]">
                      <Truck className="w-3.5 h-3.5 text-[#2D5F4F]" />
                      <span data-testid="scp-reco-weight">{recommendation.total_weight_kg} kg · {recommendation.total_pieces} pcs{recommendation.state ? ` · ${recommendation.state}` : ''}</span>
                    </div>
                    {recommendation.recommended_transport_id ? (
                      <div className="flex items-start gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                        <div>
                          <span className="text-sm font-semibold text-[#2D5F4F]" data-testid="scp-reco-name">{recommendation.recommended_transport_name}</span>
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-[#8A8F9E]">{recommendation.selection_basis === 'customer_tagged' ? 'Customer default' : 'Best for state'}</span>
                          {recommendation.reason && <p className="text-xs text-gray-600" data-testid="scp-reco-reason">{recommendation.reason}</p>}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700">No auto-recommendation — please pick a transport manually.</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-[#8A8F9E]">Transport suggestion will appear here.</p>
                )}
              </div>
              <Select value={transportId} onValueChange={setTransportId}>
                <SelectTrigger data-testid="scp-transport-select">
                  <SelectValue placeholder={transporters.length ? 'Select transport' : 'No transporters found'} />
                </SelectTrigger>
                <SelectContent>
                  {transporters.map((t) => {
                    const isReco = t.transport_id === recommendation?.recommended_transport_id;
                    return (
                      <SelectItem key={t.transport_id} value={t.transport_id} data-testid={`scp-transport-${t.transport_id}`}>
                        {t.name}{isReco ? '  ★ recommended' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="scp-dialog-cancel">Cancel</Button>
            <Button onClick={create} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="scp-dialog-create">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalCreatePickSlip;
